/**
 * PORT (wide screens): the worms' name and health labels in a battle (the `worm` sprite's worm_namebox / worm_name and
 * worm_hpbox / worm_hp above the worm, Arial 10 px): the battle's world is drawn at the stage's scale, 0.53-0.69 CSS px
 * per px on a phone in landscape, which made their text 5-7 CSS px, the health numbers unreadable, while the HUD around
 * them grew. With a wide host the labels are drawn larger, their text about LABEL_CSS CSS px (at most LABEL_MAX times
 * the original), still right above the worm: they grow upwards from the health box's lower edge. Drawing only: the game
 * keeps writing their text and the boxes' widths (obj.worm_modify_bars), nothing reads the labels' size.
 */
import { Point } from '@flash';

/** The labels' text size aimed at (CSS px) and the most they grow. */
export const LABEL_CSS = 11;
export const LABEL_MAX = 2;
/** The labels' own text size (px as the original draws it). */
const LABEL_PX = 10;
/** The room the labels of a worm at a side of the screen keep to its edge (CSS px). */
export const EDGE_CSS = 2;

/** The label children of a worm's sprite. */
const LABELS = ['worm_namebox', 'worm_name', 'worm_hpbox', 'worm_hp'] as const;

/** The scale of the labels on a stage (1: no wide host, or a screen where they measure LABEL_CSS already). */
export function wormLabelScale(stage: any): number {
  if (!stage || !stage.$visibleArea) return 1;
  const css = stage.cssScale > 0 ? stage.cssScale : 1;
  return Math.max(1, Math.min(LABEL_MAX, LABEL_CSS / (LABEL_PX * css)));
}

/**
 * The labels of a worm's `sprite` drawn at scale `k` about the lower edge of its health box. `fresh`: the game has just
 * set the boxes' widths (their scaleX), which are then the widths it wants at scale 1.
 */
export function scaleWormLabels(sprite: any, k: number, fresh: boolean): void {
  if (!sprite) return;
  const parts = LABELS.map((n) => sprite[n]).filter((c) => c && typeof c.scaleY === 'number');
  if (parts.length !== LABELS.length) return;
  for (const c of parts) {
    if (!c.$label) c.$label = { x: c.x, y: c.y, sx: c.scaleX, sy: c.scaleY, k: 1 };
    else if (fresh && c.name.endsWith('box')) c.$label.sx = c.scaleX; // (the width the game just set)
  }
  const hp = sprite.worm_hpbox.$label;
  // The health box's lower edge (its registration point is its middle).
  const anchor = hp.y + (sprite.worm_hpbox.height / (sprite.worm_hpbox.scaleY || 1)) * hp.sy * 0.5;
  const lift = Number(sprite.$labelLift) || 0;
  const shift = Number(sprite.$labelShift) || 0;
  for (const c of parts) {
    const o = c.$label;
    o.k = k;
    o.by = anchor + (o.y - anchor) * k;
    c.scaleX = o.sx * k;
    c.scaleY = o.sy * k;
    c.x = o.x * k + shift;
    c.y = o.by - lift;
  }
}

/**
 * The labels of worms standing close together kept apart: where two worms' labels (their name and health boxes) would
 * overlap on the screen, the higher one goes up above the other (the lower worm's stay where they are), and back down
 * when they no longer would. A worm shown at a side of the screen keeps its labels on the screen: they move in from
 * that side (EDGE_CSS CSS px from it), at most half their width (still over the worm). Called every frame of a battle;
 * drawing only.
 */
export function separateWormLabels(sprites: any[]): void {
  const blocks: Array<{ s: any; x: number; y: number; w: number; h: number; lift: number; shift: number }> = [];
  for (const s of sprites) {
    const box = s && s.worm_namebox;
    const hp = s && s.worm_hpbox;
    if (!box || !hp || !s.stage || !box.$label || !box.visible || !hp.visible) continue;
    let shown = true;
    for (let a = s; a && shown; a = a.parent) if (!a.visible) shown = false;
    if (!shown) continue;
    const stage = s.stage;
    const a = box.getBounds(stage);
    const b = hp.getBounds(stage);
    // (the sprite's px in stage px along x: its labels' shift is kept in the sprite's coordinates)
    const o0 = s.localToGlobal(new Point(0, 0));
    const unit = s.localToGlobal(new Point(1, 0)).x - o0.x || 1;
    const was = (Number(s.$labelShift) || 0) * unit;
    // (where they are without a lift and a shift)
    const x = Math.min(a.x, b.x) - was;
    const y = Math.min(a.y, b.y);
    const w = Math.max(a.x + a.width, b.x + b.width) - was - x;
    const lift = Number(s.$labelLift) || 0;
    // In from a side of the shown area, while the worm (the labels' middle) is on the screen.
    let shift = 0;
    const R = stage.safeRect ?? stage.visibleRect;
    if (R && R.width > 0) {
      const m = EDGE_CSS / (stage.cssScale > 0 ? stage.cssScale : 1);
      const mid = x + w / 2;
      if (mid >= R.x && mid <= R.x + R.width) {
        if (x < R.x + m) shift = Math.min(R.x + m - x, w / 2);
        else if (x + w > R.x + R.width - m) shift = -Math.min(x + w - (R.x + R.width - m), w / 2);
      }
    }
    blocks.push({ s, x: x + shift, y: y + lift, w, h: Math.max(a.y + a.height, b.y + b.height) - y, lift: 0, shift: shift / unit });
  }
  // The lowest first: each keeps clear of those placed before it by going up.
  blocks.sort((p, q) => q.y + q.h - (p.y + p.h) || p.x - q.x);
  const placed: typeof blocks = [];
  for (const b of blocks) {
    for (let n = 0; n < blocks.length; n++) {
      const top = b.y - b.lift;
      const hit = placed.find((p) => b.x < p.x + p.w && p.x < b.x + b.w && top < p.y - p.lift + p.h && p.y - p.lift < top + b.h);
      if (!hit) break;
      b.lift = b.y + b.h - (hit.y - hit.lift) + 1;
    }
    placed.push(b);
  }
  for (const b of blocks) {
    const lift = Math.round(b.lift);
    const shift = Math.round(b.shift * 4) / 4;
    if ((Number(b.s.$labelLift) || 0) === lift && (Number(b.s.$labelShift) || 0) === shift) continue;
    b.s.$labelLift = lift;
    b.s.$labelShift = shift;
    for (const n of LABELS) {
      const c = b.s[n];
      if (!c || !c.$label || typeof c.$label.by !== 'number') continue;
      c.y = c.$label.by - lift;
      c.x = c.$label.x * c.$label.k + shift;
    }
  }
}
